import React from "react";
import Seo from "../components/Seo.jsx";

/* Conversations — the replies you've posted to Reddit through Community Ninja,
   with their reply state. Data comes from /reddit/conversations (one row per
   app-created comment, previews only — never full threads). The backend
   lazily refreshes reply state from Reddit when this page loads. */

function StatCard({ label, value, suffix, tone }) {
  return (
    <div
      className="card"
      style={{
        flex: "1 1 0",
        minWidth: 0,
        padding: "16px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <div style={{
        fontSize: 11.5, fontWeight: 600, letterSpacing: "0.04em",
        textTransform: "uppercase", color: "var(--text-tertiary)",
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 28, fontWeight: 700, lineHeight: 1.1,
        color: tone || "var(--text-primary)",
        display: "flex", alignItems: "baseline", gap: 2,
      }}>
        {value}
        {suffix && (
          <span style={{ fontSize: 16, fontWeight: 600, color: "var(--text-tertiary)" }}>
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

function Quote({ children }) {
  return (
    <div className="thread-excerpt" style={{ marginTop: 4 }}>
      <span className="quote-mark">“</span>
      <div style={{ paddingLeft: 14 }}>{children}</div>
    </div>
  );
}

function ConversationCard({ c, onRequestDelete }) {
  const sub = c.subreddit
    ? (c.subreddit.startsWith("r/") ? c.subreddit : `r/${c.subreddit}`)
    : "r/unknown";
  const when = window.relativeAgo ? window.relativeAgo(c.posted_at) : "";
  const replied = c.has_reply;
  const replyCountLabel =
    replied && c.reply_count > 1 ? ` · ${c.reply_count} replies` : "";

  return (
    <div className="card" style={{ padding: 18 }}>
      {/* Header row */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        flexWrap: "wrap", marginBottom: 12,
      }}>
        <span className="opp-brief-sub" style={{ fontWeight: 600 }}>{sub}</span>
        <span className="opp-brief-sep">·</span>
        <span style={{ fontSize: 12.5, color: "var(--text-tertiary)" }}>{when}</span>
        <div style={{ flex: 1 }} />
        <span
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            fontSize: 11, fontWeight: 700, letterSpacing: "0.03em",
            textTransform: "uppercase",
            padding: "3px 9px", borderRadius: 999,
            background: replied ? "var(--success-subtle)" : "var(--bg-sunken)",
            color: replied ? "var(--success-text)" : "var(--text-tertiary)",
            border: "1px solid",
            borderColor: replied ? "var(--success-border)" : "var(--border)",
          }}
        >
          {replied ? <window.I.Comment size={11} /> : <window.I.Clock size={11} />}
          {replied ? `Replied${replyCountLabel}` : "Awaiting reply"}
        </span>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => onRequestDelete?.(c)}
          aria-label="Delete conversation"
          title="Delete this conversation"
          style={{ padding: "4px 8px", color: "var(--text-tertiary)" }}
        >
          <window.I.Trash size={14} />
        </button>
      </div>

      {/* Your comment */}
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
        Your comment
      </div>
      <Quote>{c.comment_preview || "(no preview)"}</Quote>

      {/* Latest reply or empty state */}
      <div style={{ marginTop: 14 }}>
        {replied ? (
          <>
            <div style={{ fontSize: 12.5, color: "var(--text-secondary)" }}>
              Latest reply{c.latest_reply_author ? <> from <strong style={{ color: "var(--text-primary)" }}>u/{c.latest_reply_author}</strong></> : null}:
            </div>
            <Quote>{c.latest_reply_preview || "(no preview)"}</Quote>
          </>
        ) : (
          <div style={{ fontSize: 12.5, color: "var(--text-tertiary)", fontStyle: "italic" }}>
            No replies yet
          </div>
        )}
      </div>

      {/* Footer link */}
      {c.permalink && (
        <div style={{ marginTop: 14 }}>
          <a
            href={c.permalink}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "var(--accent)", textDecoration: "none", fontSize: 13, fontWeight: 600 }}
          >
            Open thread →
          </a>
        </div>
      )}
    </div>
  );
}

/* Delete confirmation. Always removes the conversation from Community Ninja;
   the opt-in checkbox additionally deletes the underlying comment on Reddit
   from the user's connected account (irreversible there). */
function DeleteConfirm({ conversation, onCancel, onConfirm }) {
  // Checked by default — when someone deletes a conversation, removing the
  // actual Reddit comment is usually the primary intent. They can opt out to
  // keep the comment live and only drop it from Community Ninja.
  const [alsoReddit, setAlsoReddit] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  const sub = conversation.subreddit
    ? (conversation.subreddit.startsWith("r/") ? conversation.subreddit : `r/${conversation.subreddit}`)
    : "r/unknown";

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await onConfirm(alsoReddit);
    } catch (e) {
      setError(e?.message || "Could not delete the conversation.");
      setBusy(false);
    }
  };

  return (
    <>
      <div className="profile-creator-overlay" onClick={busy ? undefined : onCancel} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Delete conversation"
        style={{
          position: "fixed", zIndex: 90, top: "50%", left: "50%",
          transform: "translate(-50%, -50%)", width: "min(440px, calc(100vw - 32px))",
          background: "var(--bg)", border: "1px solid var(--border)",
          borderRadius: 16, boxShadow: "var(--shadow-lg)", padding: 22,
        }}
      >
        <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: "var(--text-primary)" }}>
          Delete this conversation?
        </h2>
        <p style={{ fontSize: 13.5, color: "var(--text-secondary)", lineHeight: 1.55, marginTop: 8 }}>
          Your comment in <strong>{sub}</strong> will be removed from Community Ninja.
        </p>
        <div className="thread-excerpt" style={{ marginTop: 4, marginBottom: 14 }}>
          <span className="quote-mark">“</span>
          <div style={{ paddingLeft: 14 }}>{conversation.comment_preview || "(no preview)"}</div>
        </div>

        <label
          style={{
            display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer",
            padding: "12px 14px", borderRadius: 10,
            background: "var(--bg-sunken)", border: "1px solid var(--border)",
          }}
        >
          <input
            type="checkbox"
            checked={alsoReddit}
            onChange={(e) => setAlsoReddit(e.target.checked)}
            disabled={busy}
            style={{ marginTop: 2 }}
          />
          <span style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5 }}>
            Also delete the comment on Reddit.{" "}
            <span style={{ color: "var(--text-tertiary)" }}>
              This permanently removes it from the thread and can't be undone.
            </span>
          </span>
        </label>

        {error && (
          <div style={{
            marginTop: 12, padding: "10px 12px",
            background: "var(--danger-subtle)", color: "var(--danger-text)",
            border: "1px solid var(--danger-border)", borderRadius: 8, fontSize: 12.5,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={confirm}
            disabled={busy}
            style={{
              color: "var(--danger-text)",
              borderColor: "var(--danger-border)",
              background: "var(--danger-subtle)",
            }}
          >
            <window.I.Trash size={13} />
            {busy ? "Deleting…" : (alsoReddit ? "Delete here & on Reddit" : "Delete")}
          </button>
        </div>
      </div>
    </>
  );
}

function ConversationsScreen({ onNavigate, pushToast }) {
  const userId = window.api?.auth?.readUserId?.() || null;
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState("");
  const [data, setData] = React.useState(null);
  const [note, setNote] = React.useState("");
  const [pendingDelete, setPendingDelete] = React.useState(null);

  const applyFeed = (resp) => setData({
    stats: resp?.stats || { replies: 0, total: 0, reply_rate: 0, awaiting: 0 },
    conversations: resp?.conversations || [],
  });

  // Initial load is a pure read — no Reddit calls. Reply state only updates
  // when the user clicks Refresh.
  const load = React.useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    setError("");
    try {
      applyFeed(await window.api.reddit.conversations(userId));
    } catch (e) {
      setError(e?.message || "Could not load conversations.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  React.useEffect(() => { load(); }, [load]);

  // Manual refresh: re-polls a bounded, newest-first batch from Reddit.
  const doRefresh = React.useCallback(async () => {
    if (!userId || refreshing) return;
    setRefreshing(true);
    setError("");
    setNote("");
    try {
      const resp = await window.api.reddit.refreshConversations(userId);
      applyFeed(resp);
      if (resp?.connected === false) {
        setNote("Connect Reddit in Settings to refresh reply status.");
      } else if (resp?.remaining_stale > 0) {
        setNote(`Checked the ${resp.refreshed} most recent · ${resp.remaining_stale} more not checked yet — refresh again to continue.`);
      } else if (typeof resp?.refreshed === "number") {
        setNote(resp.refreshed > 0 ? `Up to date · checked ${resp.refreshed}.` : "Already up to date.");
      }
    } catch (e) {
      setError(e?.message || "Could not refresh conversations.");
    } finally {
      setRefreshing(false);
    }
  }, [userId, refreshing]);

  // Remove a deleted conversation locally and recompute stats, mirroring the
  // backend's feed math so the numbers stay correct without a refetch.
  const confirmDelete = React.useCallback(async (alsoReddit) => {
    const c = pendingDelete;
    if (!c) return;
    await window.api.reddit.deleteConversation(c.id, { onReddit: alsoReddit });
    setData((prev) => {
      const remaining = (prev?.conversations || []).filter((x) => x.id !== c.id);
      const total = remaining.length;
      const replies = remaining.filter((x) => x.has_reply).length;
      return {
        conversations: remaining,
        stats: {
          replies,
          total,
          reply_rate: total ? Math.round((replies / total) * 100) : 0,
          awaiting: total - replies,
        },
      };
    });
    setPendingDelete(null);
    pushToast?.(
      alsoReddit ? "Conversation deleted here and on Reddit." : "Conversation deleted.",
    );
  }, [pendingDelete, pushToast]);

  const stats = data?.stats || { replies: 0, total: 0, reply_rate: 0, awaiting: 0 };
  const conversations = data?.conversations || [];
  const isEmpty = !loading && conversations.length === 0;

  return (
    <div className="page screen-enter" style={{ maxWidth: 1080 }}>
      <Seo title="Conversations" noindex />
      <div className="page-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div>
          <h1 className="page-title">Responses</h1>
          <p className="page-subtitle">
            Replies you've posted to Reddit.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={doRefresh}
          disabled={loading || refreshing}
          title="Re-check replies from Reddit (newest first)"
        >
          <window.I.Refresh size={13} style={refreshing ? { animation: "spin 900ms linear infinite" } : null} />
          {refreshing ? "Checking…" : "Refresh"}
        </button>
      </div>

      {note && (
        <div style={{
          fontSize: 12.5, color: "var(--text-tertiary)", marginBottom: 14,
          marginTop: -6,
        }}>
          {note}
        </div>
      )}

      {/* Stats row */}
      <div style={{ display: "flex", gap: 12, marginBottom: 22, flexWrap: "wrap" }}>
        {loading ? (
          [0, 1, 2].map((i) => (
            <div key={i} className="card" style={{ flex: "1 1 0", minWidth: 0, padding: "16px 18px" }}>
              <div className="skel" style={{ height: 12, width: "55%" }} />
              <div className="skel" style={{ height: 26, width: "35%", marginTop: 10 }} />
            </div>
          ))
        ) : (
          <>
            <StatCard label="Replies" value={stats.replies} tone="var(--success-text)" />
            <StatCard label="Reply Rate" value={stats.reply_rate} suffix="%" />
            <StatCard label="Awaiting Reply" value={stats.awaiting} />
          </>
        )}
      </div>

      {error && (
        <div style={{
          padding: "10px 12px", marginBottom: 16,
          background: "var(--danger-subtle)", color: "var(--danger-text)",
          border: "1px solid var(--danger-border)", borderRadius: 8, fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {[0, 1].map((i) => (
            <div key={i} className="card" style={{ padding: 18 }}>
              <div className="skel" style={{ height: 14, width: "30%" }} />
              <div className="skel" style={{ height: 12, width: "85%", marginTop: 14 }} />
              <div className="skel" style={{ height: 12, width: "70%", marginTop: 8 }} />
            </div>
          ))}
        </div>
      ) : isEmpty ? (
        <div className="card" style={{ padding: "40px 24px", textAlign: "center" }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, margin: "0 auto 14px",
            display: "grid", placeItems: "center",
            background: "var(--bg-sunken)", color: "var(--text-tertiary)",
            border: "1px solid var(--border)",
          }}>
            <window.I.Comment size={20} />
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>
            No conversations yet
          </div>
          <div style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 6, lineHeight: 1.55, maxWidth: 420, marginInline: "auto" }}>
            Open an opportunity, click <strong>Generate Response</strong>, and post a reply.
            It'll show up here so you can track who responds.
          </div>
          {onNavigate && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              style={{ marginTop: 16 }}
              onClick={() => onNavigate("opportunities")}
            >
              <window.I.Inbox size={13} />
              Go to Opportunities
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {conversations.map((c) => (
            <ConversationCard key={c.id} c={c} onRequestDelete={setPendingDelete} />
          ))}
        </div>
      )}

      {pendingDelete && (
        <DeleteConfirm
          conversation={pendingDelete}
          onCancel={() => setPendingDelete(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

window.ConversationsScreen = ConversationsScreen;
